import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CopyMode,
  getMoldOrderFirstLevelType,
  MoldOrder,
} from '../../types/mold';
import { Plus, Copy, FileText, ClipboardList } from 'lucide-react';
import { ROUTES } from '../../constants/routes';
import Modal from './Modal';

const CreateNewMoldOrderButton: React.FC<{ moldOrder: MoldOrder }> = ({
  moldOrder,
}) => {
  const navigate = useNavigate();
  const [showChoice, setShowChoice] = useState(false);

  // The follow-up always targets the SAME mold, so copy is always applicable here; the
  // cross-mold hide guard is for other entry points + the backend.
  const go = (copyMode: CopyMode) => {
    // Preserve the source's EXACT type: pass the first-level group as `type` and the
    // exact subtype as `subType` so the new order opens on the correct form.
    const params = new URLSearchParams({
      selected: String(moldOrder.mold.id),
      parentOrderId: String(moldOrder.id),
      type: getMoldOrderFirstLevelType(moldOrder.type),
      subType: moldOrder.type,
      copyMode,
    });
    navigate(`${ROUTES.CREATE_MOLD_ORDERS}?${params.toString()}`);
  };

  return (
    <>
      <button
        onClick={() => setShowChoice(true)}
        data-cy='create-new-mold-order'
        className='shadow-xl text-base w-full py-3 px-4 rounded-md flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
      >
        <Plus size={20} className='mr-2' />
        <span>สร้างใบงานใหม่</span>
      </button>

      {showChoice && (
        <Modal
          onClose={() => setShowChoice(false)}
          maxWidth='max-w-md'
          isBackgroundTransparent
          testId='copy-choice-modal'
        >
          <div className='p-1'>
            <h2 className='text-lg font-bold text-gray-800 mb-1'>
              สร้างใบงานใหม่จากใบงาน #{moldOrder.id}
            </h2>
            <p className='text-sm text-gray-500 mb-4'>
              เลือกว่าจะนำข้อมูลเดิมมาด้วยมากน้อยแค่ไหน
            </p>
            <div className='flex flex-col gap-3'>
              {/* Default option (blank) is visually primary — copy is an explicit opt-in. */}
              <button
                onClick={() => go('none')}
                data-cy='choice-blank'
                className='text-left border-2 border-blue-500 bg-blue-50/40 rounded-lg p-4 hover:bg-blue-50 transition-colors'
              >
                <div className='flex items-center gap-3'>
                  <FileText className='w-5 h-5 text-blue-600 flex-shrink-0' />
                  <div>
                    <div className='font-semibold text-gray-800'>
                      กรอกใหม่ทั้งหมด
                    </div>
                    <div className='text-xs text-gray-500'>
                      เริ่มจากฟอร์มเปล่า
                    </div>
                  </div>
                </div>
              </button>
              <button
                onClick={() => go('metadata')}
                data-cy='choice-copy-metadata'
                className='text-left border-2 border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors'
              >
                <div className='flex items-center gap-3'>
                  <ClipboardList className='w-5 h-5 text-gray-600 flex-shrink-0' />
                  <div>
                    <div className='font-semibold text-gray-800'>
                      คัดลอกเฉพาะข้อมูลสำหรับสร้างใบงาน
                    </div>
                    <div className='text-xs text-gray-500'>
                      ดึงหมายเหตุและความสำคัญมาให้ · ฟอร์มและรูปภาพต้องกรอกใหม่
                    </div>
                  </div>
                </div>
              </button>
              <button
                onClick={() => go('all')}
                data-cy='choice-copy-all'
                className='text-left border-2 border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors'
              >
                <div className='flex items-center gap-3'>
                  <Copy className='w-5 h-5 text-gray-600 flex-shrink-0' />
                  <div>
                    <div className='font-semibold text-gray-800'>
                      คัดลอกข้อมูลทั้งหมดมาด้วย
                    </div>
                    <div className='text-xs text-gray-500'>
                      ดึงข้อมูลฟอร์ม รูปภาพ และข้อความที่กรอกไว้มาให้ แก้ไขได้ภายหลัง
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default CreateNewMoldOrderButton;
